import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, proxyImg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Loader, Overline } from "@/components/shared";
import { Field, inputCls, slugify } from "@/components/cms/kit";
import { ToolForm, ArticleForm, ComparisonForm } from "@/components/cms/forms";
import { ResearchInbox } from "@/components/cms/ResearchInbox";
import { SuggestionsInbox } from "@/components/cms/SuggestionsInbox";
import { AdvertisingInbox } from "@/components/cms/AdvertisingInbox";
import { AnalyticsPanel } from "@/components/cms/AnalyticsPanel";
import {
  LogOut, Plus, Trash2, Pencil, X, Upload, ImageIcon, Copy, Power, Search, AlertTriangle, Rss, Loader2,
} from "lucide-react";
import { AD_PLACEMENTS, PLACEMENT_MAP, RATIO_TOLERANCE } from "@/lib/adPlacements";
import { CONTENT_TYPES, isCanonicalType } from "@/lib/taxonomy";

const COLLS = ["tools", "articles", "comparisons", "models", "deals", "ads"];
const TEMPLATES = {
  tools: { slug: "", name: "", domain: "", category: "AI Assistants", subcategory: "", pricing_model: "Freemium", price: "", rating: 8, tagline: "", best_for: [], features: [], pros: [], cons: [], body_sections: [], faqs: [], image: "", logo: "" },
  articles: { slug: "", title: "", subtitle: "", category: "AI", subcategory: "", content_type: "News", author: "KLYROO Staff", tags: [], key_takeaways: [], intelligence: { happened: "", matters: "", affected: "", know: "", next: "" }, body_sections: [], faqs: [], image: "" },
  comparisons: { slug: "", title: "", category: "AI Models", items: [], columns: [], rows: [], quick_answer: {}, body_sections: [], faqs: [], image: "" },
  models: { slug: "", name: "", company: "", release: "2026", model_type: "", context: "", reasoning: "", coding: "", multimodal: "Yes", pricing: "", best_use: "", rating: 8, specs: {}, body_sections: [] },
  deals: { slug: "", product_name: "", retailer: "Amazon", country: "US", category: "Tech", current_price: "", previous_price: "", discount_percent: 0, currency: "USD", product_url: "", image: "", is_test: true, status: "active" },
  ads: { slug: "", name: "", placement: "home-top", sponsor: "", headline: "", subtext: "", cta: "Learn more", link: "", image: "", active: true },
};

const titleOf = (it) => it.name || it.title || it.product_name || it.slug;
const statusOf = (it, coll) => {
  if (coll === "ads") return it.active === false ? "inactive" : (it.status || "published");
  const s = it.status || "published";
  return s === "active" ? "published" : s; // legacy deals used "active"
};
const STATUS_STYLE = {
  published: "bg-klyroo-cyan/15 text-klyroo-cyan", active: "bg-klyroo-cyan/15 text-klyroo-cyan",
  draft: "bg-amber-400/15 text-amber-300", inactive: "bg-white/5 text-slate-400", expired: "bg-klyroo-red/15 text-klyroo-red",
};

export default function Admin() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [coll, setColl] = useState("tools");
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [ingesting, setIngesting] = useState(false);
  const [sort, setSort] = useState("updated");

  useEffect(() => { if (ready && !user) nav("/login"); }, [ready, user, nav]);
  const loadStats = useCallback(() => { api.adminStats().then(setStats).catch(() => {}); }, []);
  const loadSeq = useRef(0);
  const load = useCallback(() => {
    setItems(null);
    const seq = ++loadSeq.current;  // ignore stale responses when switching collections
    if (coll === "homepage" || coll === "llm" || coll === "research" || coll === "suggestions" || coll === "advertising" || coll === "analytics") { setItems([]); loadStats(); return; }
    api.adminList(coll).then((d) => { if (seq === loadSeq.current) setItems(d.items); })
      .catch(() => { if (seq === loadSeq.current) setItems([]); });
    loadStats();
  }, [coll, loadStats]);
  useEffect(() => { if (user) load(); }, [user, load]);
  useEffect(() => { setQ(""); setStatusFilter("all"); setCatFilter("all"); setTypeFilter("all"); setSort("updated"); }, [coll]);

  // Phase 5: open an Article draft in the existing editor from the Research tab.
  useEffect(() => {
    const handler = async (e) => {
      const slug = e.detail?.slug;
      if (!slug) return;
      setColl("articles");
      try {
        const art = await api.adminGetOne("articles", slug);
        if (art) setEditing(structuredClone(art));
        else toast.error("Draft article not found");
      } catch { toast.error("Could not open draft"); }
    };
    window.addEventListener("klyroo:open-article", handler);
    return () => window.removeEventListener("klyroo:open-article", handler);
  }, []);

  const categories = useMemo(() => [...new Set((items || []).map((i) => i.category).filter(Boolean))].sort(), [items]);

  const filtered = useMemo(() => {
    let list = [...(items || [])];
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter((i) => [titleOf(i), i.slug, i.category, i.sponsor, i.company, i.retailer, i.brand].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
    }
    if (statusFilter !== "all") list = list.filter((i) => statusOf(i, coll) === statusFilter);
    if (catFilter !== "all") list = list.filter((i) => i.category === catFilter);
    if (typeFilter !== "all") {
      list = typeFilter === "__unclassified"
        ? list.filter((i) => !isCanonicalType(i.content_type))
        : list.filter((i) => String(i.content_type || "").toLowerCase() === typeFilter.toLowerCase());
    }
    const cmp = {
      updated: (a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""),
      newest: (a, b) => (b.published_at || "").localeCompare(a.published_at || ""),
      oldest: (a, b) => (a.published_at || "").localeCompare(b.published_at || ""),
      alpha: (a, b) => titleOf(a).localeCompare(titleOf(b)),
      impressions: (a, b) => (b.impressions || 0) - (a.impressions || 0),
      clicks: (a, b) => (b.clicks || 0) - (a.clicks || 0),
      ctr: (a, b) => (b.clicks / (b.impressions || 1)) - (a.clicks / (a.impressions || 1)),
    };
    list.sort(cmp[sort] || cmp.updated);
    return list;
  }, [items, q, statusFilter, catFilter, typeFilter, sort, coll]);

  if (!ready || !user) return <div className="min-h-screen bg-klyroo-bg"><Loader /></div>;

  const doDelete = async () => {
    const it = confirmDel; setConfirmDel(null);
    await api.adminDelete(coll, it.slug);
    toast.success("Deleted permanently");
    load();
  };

  const ingestDeals = async () => {
    setIngesting(true);
    try {
      const r = await api.ingestDeals({ max_searches: 9, per_query: 10 });
      const rep = r.report;
      toast.success(`Fetched ${rep.stored} live deals · ${rep.valid_results} valid / ${rep.rejected_results} rejected · ${rep.searches_used} searches used`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deal ingestion failed");
    } finally { setIngesting(false); }
  };

  const toggleActive = async (it) => {
    const cur = statusOf(it, coll);
    const activate = cur !== "published" && cur !== "active";
    const patch = coll === "ads" ? { ...it, active: activate } : { ...it, status: activate ? "published" : "inactive" };
    delete patch.__new;
    await api.adminUpsert(coll, patch);
    toast.success(activate ? "Activated" : "Deactivated");
    load();
  };

  const duplicate = async (it) => {
    const copy = structuredClone(it);
    delete copy.__new;
    copy.slug = `${it.slug}-copy-${Date.now().toString().slice(-4)}`;
    if (coll === "ads") { copy.active = false; copy.impressions = 0; copy.clicks = 0; copy.name = `${it.name || ""} (copy)`; }
    else { copy.status = "draft"; if (copy.title) copy.title = `${copy.title} (copy)`; if (copy.name) copy.name = `${copy.name} (copy)`; }
    await api.adminUpsert(coll, copy);
    toast.success("Duplicated as draft");
    load();
    setEditing(copy);
  };

  const sortOptions = coll === "ads"
    ? [["updated", "Recently updated"], ["impressions", "Most impressions"], ["clicks", "Most clicks"], ["ctr", "Highest CTR"], ["alpha", "A–Z"]]
    : [["updated", "Recently updated"], ["newest", "Newest"], ["oldest", "Oldest"], ["alpha", "A–Z"]];
  const statusFilters = coll === "deals"
    ? ["all", "published", "draft", "inactive", "expired"]
    : ["all", "published", "draft", "inactive"];

  return (
    <div className="min-h-screen bg-klyroo-bg">
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><Logo size={28} /><span className="overline hidden sm:block">CMS Dashboard</span></div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400 hidden sm:block">{user.email}</span>
            <Link to="/" className="text-sm text-slate-300 hover:text-white">View site</Link>
            <button onClick={() => { logout(); nav("/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm text-klyroo-red hover:text-white"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-3xl font-extrabold text-white tracking-tight">Content Manager</h1>

        {/* summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
          {stats && COLLS.map((c) => (
            <button key={c} onClick={() => setColl(c)} data-testid={`stat-card-${c}`}
              className={`card-surface rounded-2xl p-4 text-left transition ${coll === c ? "ring-1 ring-klyroo-cyan" : "hover:bg-white/[0.06]"}`}>
              <div className="font-display text-2xl font-extrabold text-white">{stats[c].total}</div>
              <Overline className="mt-1">{c}</Overline>
              <div className="mt-2 flex flex-wrap gap-1 text-[10px] font-mono">
                <span className="text-klyroo-cyan">{stats[c].published} live</span>
                {stats[c].draft > 0 && <span className="text-amber-300">· {stats[c].draft} draft</span>}
                {stats[c].inactive > 0 && <span className="text-slate-500">· {stats[c].inactive} off</span>}
              </div>
            </button>
          ))}
        </div>

        {/* ad performance + recent */}
        {stats && (
          <div className="grid lg:grid-cols-3 gap-3 mt-3">
            <div className="card-surface rounded-2xl p-4 flex items-center justify-around" data-testid="ad-performance">
              <Stat label="Ad Impressions" value={(stats.ad_performance.impressions || 0).toLocaleString()} />
              <Stat label="Ad Clicks" value={(stats.ad_performance.clicks || 0).toLocaleString()} />
              <Stat label="Overall CTR" value={`${stats.ad_performance.ctr}%`} accent />
            </div>
            <div className="card-surface rounded-2xl p-4 lg:col-span-2">
              <Overline className="mb-2">Recently updated</Overline>
              <div className="flex flex-col gap-1.5">
                {(stats.recent || []).slice(0, 5).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <span className="text-[10px] font-mono uppercase text-slate-500 w-20 shrink-0">{r.coll}</span>
                    <span className="text-slate-200 truncate flex-1">{r.title}</span>
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${STATUS_STYLE[r.status] || "text-slate-500"}`}>{r.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* tabs + new */}
        <div className="flex items-center justify-between mt-8 mb-4 flex-wrap gap-3">
          <div className="flex gap-2 flex-wrap">
            {COLLS.map((c) => (
              <button key={c} onClick={() => setColl(c)} data-testid={`admin-tab-${c}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${coll === c ? "bg-klyroo-cyan text-black" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>{c}</button>
            ))}
            <button onClick={() => setColl("homepage")} data-testid="admin-tab-homepage"
              className={`px-4 py-2 rounded-lg text-sm font-medium ${coll === "homepage" ? "bg-klyroo-cyan text-black" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>Homepage</button>
            <button onClick={() => setColl("llm")} data-testid="admin-tab-llm"
              className={`px-4 py-2 rounded-lg text-sm font-medium ${coll === "llm" ? "bg-klyroo-cyan text-black" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>AI / LLM</button>
            <button onClick={() => setColl("research")} data-testid="admin-tab-research"
              className={`px-4 py-2 rounded-lg text-sm font-medium ${coll === "research" ? "bg-klyroo-cyan text-black" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>Research</button>
            <button onClick={() => setColl("suggestions")} data-testid="admin-tab-suggestions"
              className={`px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${coll === "suggestions" ? "bg-klyroo-cyan text-black" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>Suggestions
              {stats?.suggestions_new > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-klyroo-red text-white" data-testid="suggestions-new-count">{stats.suggestions_new}</span>}
            </button>
            <button onClick={() => setColl("advertising")} data-testid="admin-tab-advertising"
              className={`px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 ${coll === "advertising" ? "bg-klyroo-cyan text-black" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>Advertising
              {stats?.advertising_new > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-klyroo-red text-white" data-testid="advertising-new-count">{stats.advertising_new}</span>}
            </button>
            <button onClick={() => setColl("analytics")} data-testid="admin-tab-analytics"
              className={`px-4 py-2 rounded-lg text-sm font-medium ${coll === "analytics" ? "bg-klyroo-cyan text-black" : "bg-white/5 text-slate-300 hover:bg-white/10"}`}>Analytics</button>
          </div>
          {coll !== "homepage" && coll !== "llm" && coll !== "research" && coll !== "suggestions" && coll !== "advertising" && coll !== "analytics" && (
            <button onClick={() => setEditing({ ...structuredClone(TEMPLATES[coll]), __new: true })} data-testid="admin-new"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white text-black text-sm font-semibold hover:bg-klyroo-cyan"><Plus size={15} /> New</button>
          )}
        </div>

        {coll === "homepage" ? (
          <HomepageCuration />
        ) : coll === "llm" ? (
          <LlmSettings />
        ) : coll === "research" ? (
          <ResearchInbox />
        ) : coll === "suggestions" ? (
          <SuggestionsInbox onChange={loadStats} />
        ) : coll === "advertising" ? (
          <AdvertisingInbox onChange={loadStats} />
        ) : coll === "analytics" ? (
          <AnalyticsPanel />
        ) : (
        <>
        {/* toolbar: search / filters / sort */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, slug, category…" data-testid="admin-search"
              className={`${inputCls} pl-9`} />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="admin-filter-status" className={`${inputCls} w-auto`}>
            {statusFilters.map((s) => <option key={s} value={s}>{s === "all" ? "All statuses" : s}</option>)}
          </select>
          {categories.length > 0 && (
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} data-testid="admin-filter-category" className={`${inputCls} w-auto`}>
              <option value="all">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          {coll === "articles" && (
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="admin-filter-type" className={`${inputCls} w-auto`}>
              <option value="all">All types</option>
              {CONTENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              <option value="__unclassified">⚠ Needs classification</option>
            </select>
          )}
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="admin-sort" className={`${inputCls} w-auto`}>
            {sortOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {coll === "deals" && (
            <button onClick={ingestDeals} disabled={ingesting} data-testid="admin-ingest-deals"
              className="px-3.5 py-2 rounded-lg bg-klyroo-cyan text-black text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50 flex items-center gap-1.5">
              {ingesting ? <Loader2 size={14} className="animate-spin" /> : <Rss size={14} />} {ingesting ? "Fetching…" : "Fetch Live Deals"}
            </button>
          )}
        </div>

        {/* list */}
        {!items ? <Loader /> : (
          <div className="card-surface rounded-2xl divide-y divide-white/5">
            {filtered.map((it) => {
              const st = statusOf(it, coll);
              return (
                <div key={it.slug} className="flex items-center gap-3 p-4" data-testid={`admin-row-${it.slug}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium truncate">{titleOf(it)}</div>
                    <div className="text-xs font-mono text-slate-500 truncate">{it.slug} · {it.category || (it.placement && PLACEMENT_MAP[it.placement]?.label) || it.placement || it.company || ""}</div>
                  </div>
                  {coll === "articles" && (
                    <div className="hidden sm:flex items-center gap-1.5 mr-1" data-testid={`taxonomy-${it.slug}`}>
                      {it.category && <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-klyroo-cyan/10 text-klyroo-cyan" data-testid={`cat-badge-${it.slug}`}>{it.category}</span>}
                      {isCanonicalType(it.content_type)
                        ? <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-slate-300" data-testid={`type-badge-${it.slug}`}>{it.content_type}</span>
                        : <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-400/15 text-amber-300" title={it.content_type ? `Non-standard type: ${it.content_type}` : "No content type set"} data-testid={`type-badge-${it.slug}`}>⚠ {it.content_type || "No type"}</span>}
                    </div>
                  )}
                  {coll === "ads" && (
                    <div className="hidden md:flex items-center gap-4 mr-1 text-right" data-testid={`ad-stats-${it.slug}`}>
                      <Stat label="Impr" value={(it.impressions || 0).toLocaleString()} />
                      <Stat label="Clicks" value={(it.clicks || 0).toLocaleString()} />
                      <Stat label="CTR" value={`${ctr(it.impressions, it.clicks)}%`} accent />
                    </div>
                  )}
                  {coll === "deals" && (
                    <div className="hidden md:flex items-center gap-1.5 mr-1" data-testid={`deal-meta-${it.slug}`}>
                      {it.is_test
                        ? <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-amber-400/15 text-amber-300" title="Isolated test fixture — hidden from the public feed">TEST</span>
                        : <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-400/15 text-emerald-300" title={it.source || "live"}>LIVE</span>}
                      {it.retailer && <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-white/5 text-slate-300">{it.retailer}</span>}
                      {it.country && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-slate-400">{it.country}</span>}
                      {it.discount_percent > 0 && <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-klyroo-red/15 text-klyroo-red">-{it.discount_percent}%</span>}
                      {it.quality_score != null && <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-klyroo-cyan/10 text-klyroo-cyan" title="Deal quality score">{it.quality_score}</span>}
                    </div>
                  )}
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${STATUS_STYLE[st] || "text-slate-500"}`} data-testid={`status-badge-${it.slug}`}>{st}</span>
                  <button onClick={() => setEditing(structuredClone(it))} title="Edit" data-testid={`admin-edit-${it.slug}`} className="p-2 rounded-lg text-slate-300 hover:text-klyroo-cyan hover:bg-white/5"><Pencil size={15} /></button>
                  <button onClick={() => duplicate(it)} title="Duplicate" data-testid={`admin-duplicate-${it.slug}`} className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-white/5"><Copy size={15} /></button>
                  <button onClick={() => toggleActive(it)} title={st === "published" || st === "active" ? "Deactivate" : "Activate"} data-testid={`admin-toggle-${it.slug}`}
                    className={`p-2 rounded-lg hover:bg-white/5 ${st === "published" || st === "active" ? "text-klyroo-cyan" : "text-slate-500 hover:text-klyroo-cyan"}`}><Power size={15} /></button>
                  <button onClick={() => setConfirmDel(it)} title="Delete" data-testid={`admin-delete-${it.slug}`} className="p-2 rounded-lg text-slate-300 hover:text-klyroo-red hover:bg-white/5"><Trash2 size={15} /></button>
                </div>
              );
            })}
            {filtered.length === 0 && <p className="p-8 text-center text-slate-500 font-mono text-sm">No items match.</p>}
          </div>
        )}
        </>
        )}
      </div>

      {editing && <Editor coll={coll} data={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {confirmDel && (
        <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setConfirmDel(null)}>
          <div className="w-full max-w-md card-surface rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm">
            <div className="flex items-center gap-3 mb-3"><AlertTriangle className="text-klyroo-red" size={22} /><span className="font-display font-bold text-white text-lg">Delete permanently?</span></div>
            <p className="text-sm text-slate-300">Delete <span className="text-white font-semibold">"{titleOf(confirmDel)}"</span>? This permanently removes this {coll.slice(0, -1)} and its CMS data. This cannot be undone.</p>
            <p className="text-xs text-slate-500 mt-2">Tip: use <span className="text-klyroo-cyan">Deactivate</span> instead to hide it while keeping the data.</p>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 rounded-lg text-slate-300 hover:bg-white/5 text-sm">Cancel</button>
              <button onClick={doDelete} data-testid="delete-confirm-btn" className="px-4 py-2 rounded-lg bg-klyroo-red text-white font-semibold text-sm hover:bg-red-500">Delete Permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Editor({ coll, data, categories, onClose, onSaved }) {
  if (coll === "tools") return <ToolForm data={data} categories={categories} onClose={onClose} onSaved={onSaved} />;
  if (coll === "articles") return <ArticleForm data={data} categories={categories} onClose={onClose} onSaved={onSaved} />;
  if (coll === "comparisons") return <ComparisonForm data={data} categories={categories} onClose={onClose} onSaved={onSaved} />;
  if (coll === "ads") return <AdForm data={data} onClose={onClose} onSaved={onSaved} />;
  return <JsonEditor coll={coll} data={data} onClose={onClose} onSaved={onSaved} />;
}

const ctr = (impr, clicks) => {
  const i = impr || 0, c = clicks || 0;
  return i > 0 ? ((c / i) * 100).toFixed(1) : "0.0";
};

function LlmSettings() {
  const [providers, setProviders] = useState(null);
  const [catalog, setCatalog] = useState({});
  const load = () => api.llmProviders().then((d) => setProviders(d.providers)).catch(() => setProviders([]));
  useEffect(() => {
    load();
    api.llmCatalog().then((d) => setCatalog(d.catalog || {})).catch(() => setCatalog({}));
  }, []);
  if (!providers) return <Loader />;
  return (
    <div className="space-y-5" data-testid="llm-settings">
      <p className="text-sm text-slate-400">Connect one or more LLM providers. Credentials are stored server-side and never returned to the browser. This is the integration foundation — no automatic content generation runs yet.</p>
      {providers.map((p) => <LlmCard key={p.id} p={p} models={catalog[p.id] || []} onSaved={load} />)}
      <TaskRunnerPanel providers={providers} catalog={catalog} />
      <ImageSettings />
    </div>
  );
}

function ImageSettings() {
  const [providers, setProviders] = useState(null);
  const [catalog, setCatalog] = useState({});
  const load = () => api.imageProviders().then((d) => setProviders(d.providers)).catch(() => setProviders([]));
  useEffect(() => { load(); api.imageCatalog().then((d) => setCatalog(d.catalog || {})).catch(() => setCatalog({})); }, []);
  if (!providers) return null;
  return (
    <div className="space-y-4 pt-2" data-testid="image-settings">
      <div>
        <h3 className="font-display font-bold text-white text-lg">AI Image Providers</h3>
        <p className="text-sm text-slate-400">Configure image-generation providers. Keys are stored server-side, never returned to the browser. Image generation is manual only — nothing runs automatically.</p>
      </div>
      {providers.map((p) => <ImageProviderCard key={p.id} p={p} models={catalog[p.id] || []} onSaved={load} />)}
    </div>
  );
}

function ImageProviderCard({ p, models = [], onSaved }) {
  const [enabled, setEnabled] = useState(p.enabled);
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState(p.model || p.default_model);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [st, cls] = STATUS_LABEL[p.status] || STATUS_LABEL.not_configured;
  useEffect(() => { setEnabled(p.enabled); }, [p.enabled, p.updated_at]);
  const activeModels = models.filter((m) => m.status !== "deprecated");
  const save = async () => {
    setSaving(true);
    try { await api.imageSaveProvider(p.id, { api_key: apiKey, model, enabled }); setApiKey(""); toast.success(`${p.name} image provider saved`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const toggleEnabled = async (val) => {
    if (!p.configured) { toast.error("Add and save an API key before enabling"); return; }
    setEnabled(val);
    try { await api.imageSaveProvider(p.id, { model, enabled: val }); toast.success(val ? `${p.name} enabled` : `${p.name} disabled`); onSaved(); }
    catch (e) { setEnabled(!val); toast.error("Could not update"); }
  };
  const test = async () => {
    setTesting(true); setResult(null);
    try { const r = await api.imageTestProvider(p.id); setResult(r); r.ok ? toast.success("Connection successful") : toast.error("Connection failed"); onSaved(); }
    catch (e) { setResult({ ok: false, message: e.response?.data?.detail || "Test failed" }); }
    finally { setTesting(false); }
  };
  return (
    <div className="card-surface rounded-2xl p-5" data-testid={`image-card-${p.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="font-display font-bold text-white text-lg">{p.name}</span>
          <span className={`text-[11px] font-mono ${cls}`} data-testid={`image-status-${p.id}`}>{p.configured ? st : "Not configured"}</span>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => toggleEnabled(e.target.checked)} data-testid={`image-enabled-${p.id}`} className="w-4 h-4 accent-klyroo-cyan" />
          <span className="text-sm text-slate-300">Enabled</span>
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Default image model">
          <select value={model} onChange={(e) => setModel(e.target.value)} className={inputCls} data-testid={`image-card-model-${p.id}`}>
            {activeModels.map((m) => <option key={m.id} value={m.id}>{m.display_name} — {m.id}{m.recommended ? " ★" : ""}{m.status !== "active" ? ` (${m.status})` : ""}</option>)}
          </select>
        </Field>
        <Field label="API key (write-only)"><input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={p.configured ? "•••••• (saved)" : "Paste API key"} className={inputCls} data-testid={`image-key-${p.id}`} /></Field>
      </div>
      <div className="flex items-center gap-2 mt-4">
        <button onClick={save} disabled={saving} data-testid={`image-save-${p.id}`} className="px-4 py-2 rounded-lg bg-klyroo-cyan text-black text-sm font-semibold hover:bg-white disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        <button onClick={test} disabled={testing || !p.configured} data-testid={`image-test-${p.id}`} className="px-4 py-2 rounded-lg bg-white/5 text-slate-200 text-sm hover:bg-white/10 disabled:opacity-50">{testing ? "Testing…" : "Test connection"}</button>
        {result && <span className={`text-xs font-mono ${result.ok ? "text-emerald-300" : "text-klyroo-red"}`} data-testid={`image-test-result-${p.id}`}>{result.ok ? "✓" : "✕"} {result.message}</span>}
      </div>
    </div>
  );
}

const TASK_TYPES = ["summarize", "rewrite", "generate", "classify", "extract", "structured_generate"];

function TaskRunnerPanel({ providers, catalog }) {
  const [pid, setPid] = useState(providers[0]?.id || "gemini");
  const [model, setModel] = useState("");
  const [task, setTask] = useState("summarize");
  const [system, setSystem] = useState("");
  const [prompt, setPrompt] = useState("");
  const [running, setRunning] = useState(false);
  const [res, setRes] = useState(null);

  const models = (catalog[pid] || []).filter((m) => m.status !== "deprecated");
  useEffect(() => { setModel(models[0]?.id || ""); }, [pid, catalog]); // eslint-disable-line

  const run = async () => {
    if (!prompt.trim()) return toast.error("Enter a prompt first");
    setRunning(true); setRes(null);
    try {
      const r = await api.llmRunTask({ provider: pid, model, task, system, prompt, structured: task === "structured_generate" });
      setRes(r);
      r.success ? toast.success("Task complete") : toast.error(r.error?.code || "Failed");
    } catch (e) { setRes({ success: false, error: { code: "REQUEST_FAILED", message: e.response?.data?.detail || "Request failed" } }); }
    finally { setRunning(false); }
  };

  return (
    <div className="card-surface rounded-2xl p-5 mt-2" data-testid="task-runner">
      <div className="flex items-center gap-2 mb-1"><span className="font-display font-bold text-white text-lg">Task Runner</span><span className="overline text-slate-500">developer test</span></div>
      <p className="text-sm text-slate-400 mb-4">Runs a single task against a configured provider. Executes only when you click Run — no automatic or background calls.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Provider">
          <select value={pid} onChange={(e) => setPid(e.target.value)} className={inputCls} data-testid="tr-provider">
            {providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Model">
          <select value={model} onChange={(e) => setModel(e.target.value)} className={inputCls} data-testid="tr-model">
            {models.map((m) => <option key={m.id} value={m.id}>{m.display_name}{m.status !== "active" ? ` (${m.status})` : ""}</option>)}
            {models.length === 0 && <option value="">No available models</option>}
          </select>
        </Field>
        <Field label="Task">
          <select value={task} onChange={(e) => setTask(e.target.value)} className={inputCls} data-testid="tr-task">
            {TASK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label="System instruction (optional)"><input value={system} onChange={(e) => setSystem(e.target.value)} className={inputCls} data-testid="tr-system" placeholder="Leave blank to use the task default" /></Field>
      <Field label="Prompt"><textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4} className={`${inputCls} resize-y`} data-testid="tr-prompt" placeholder="Enter the text/instruction to send" /></Field>
      <button onClick={run} disabled={running} data-testid="tr-run" className="mt-1 px-5 py-2 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm hover:bg-white disabled:opacity-60">{running ? "Running…" : "Run Test"}</button>

      {res && (
        <div className="mt-4 rounded-xl border border-white/10 bg-klyroo-bg/60 p-4" data-testid="tr-result">
          {res.success ? (
            <>
              <div className="flex flex-wrap gap-4 text-xs font-mono text-slate-400 mb-3">
                <span className="text-klyroo-cyan">✓ success</span>
                <span>{res.execution_ms} ms</span>
                {res.usage ? <span>tokens: {res.usage.totalTokens ?? "?"} (in {res.usage.inputTokens ?? "?"} / out {res.usage.outputTokens ?? "?"}{res.usage.thinkingTokens ? ` / thinking ${res.usage.thinkingTokens}` : ""})</span> : <span>tokens: n/a</span>}
              </div>
              <pre className="text-sm text-slate-200 whitespace-pre-wrap break-words max-h-72 overflow-auto">{typeof res.output === "string" ? res.output : JSON.stringify(res.output, null, 2)}</pre>
            </>
          ) : (
            <div className="text-sm" data-testid="tr-error">
              <span className="text-klyroo-red font-mono">✕ {res.error?.code}</span>
              <span className="text-slate-400 ml-2">{res.error?.message}</span>
              {res.execution_ms != null && <span className="text-slate-500 font-mono ml-2">· {res.execution_ms} ms</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL = {
  connected: ["Connected", "text-klyroo-cyan"], error: ["Connection failed", "text-klyroo-red"],
  configured: ["Configured", "text-amber-300"], not_configured: ["Not configured", "text-slate-500"],
};

function LlmCard({ p, models = [], onSaved }) {
  const initModel = p.model || p.default_model;
  const known = models.some((m) => m.id === initModel);
  const [sel, setSel] = useState(models.length ? (known ? initModel : "__custom__") : initModel);
  const [customId, setCustomId] = useState(known ? "" : (p.model || ""));
  // Once the catalog loads, resolve whether the saved model is a known preset or custom.
  useEffect(() => {
    if (!models.length) return;
    const isKnown = models.some((m) => m.id === initModel);
    setSel(isKnown ? initModel : (p.model ? "__custom__" : initModel));
    setCustomId(isKnown ? "" : (p.model || ""));
  }, [models.length]); // eslint-disable-line
  const [displayName, setDisplayName] = useState(p.display_name || "");
  const effectiveModel = sel === "__custom__" ? customId.trim() : sel;
  const selMeta = models.find((m) => m.id === sel);
  const [enabled, setEnabled] = useState(p.enabled);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [st, cls] = STATUS_LABEL[p.status] || STATUS_LABEL.not_configured;

  // Keep the card truthful: re-sync with persisted server state after any refetch.
  useEffect(() => { setEnabled(p.enabled); }, [p.enabled, p.updated_at]);

  const save = async () => {
    setSaving(true);
    try {
      await api.llmSaveProvider(p.id, { api_key: apiKey, model: effectiveModel, display_name: displayName, enabled });
      setApiKey("");
      toast.success(`${p.name} saved`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  // Enabled is persisted immediately so the toggle can never silently disagree with the DB.
  const toggleEnabled = async (val) => {
    if (!p.configured) { toast.error("Add and save an API key before enabling"); return; }
    setEnabled(val);
    try {
      await api.llmSaveProvider(p.id, { model: effectiveModel, display_name: displayName, enabled: val });
      toast.success(val ? `${p.name} enabled` : `${p.name} disabled`);
      onSaved();
    } catch (e) { setEnabled(!val); toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const test = async () => {
    setTesting(true); setResult(null);
    try { const r = await api.llmTestProvider(p.id); setResult(r); r.ok ? toast.success("Connection successful") : toast.error("Connection failed"); onSaved(); }
    catch (e) { setResult({ ok: false, message: e.response?.data?.detail || "Test failed" }); }
    finally { setTesting(false); }
  };

  return (
    <div className="card-surface rounded-2xl p-5" data-testid={`llm-card-${p.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="font-display font-bold text-white text-lg">{p.name}</span>
          <span className={`text-[11px] font-mono ${cls}`} data-testid={`llm-status-${p.id}`}>{p.configured ? st : "Not configured"}</span>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => toggleEnabled(e.target.checked)} data-testid={`llm-enabled-${p.id}`} className="w-4 h-4 accent-klyroo-cyan" />
          <span className="text-sm text-slate-300">Enabled</span>
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Display name (optional)"><input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={p.name} className={inputCls} data-testid={`llm-display-${p.id}`} /></Field>
        <Field label="Model">
          <select value={sel} onChange={(e) => setSel(e.target.value)} className={inputCls} data-testid={`llm-model-${p.id}`}>
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name} — {m.id}{m.recommended ? " ★" : ""}{m.status !== "active" ? ` (${m.status})` : ""}
              </option>
            ))}
            <option value="__custom__">Custom Model ID…</option>
          </select>
        </Field>
      </div>
      {sel === "__custom__" ? (
        <Field label="Custom Model ID">
          <input value={customId} onChange={(e) => setCustomId(e.target.value)} placeholder={p.default_model} className={inputCls} data-testid={`llm-custom-model-${p.id}`} />
        </Field>
      ) : selMeta && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400 -mt-1 mb-1" data-testid={`llm-model-meta-${p.id}`}>
          <span>{selMeta.description}</span>
          <span className="text-slate-600">·</span>
          <span className="font-mono">{selMeta.capability}</span>
          <span className={`font-mono px-1.5 py-0.5 rounded ${selMeta.status === "active" ? "bg-klyroo-cyan/15 text-klyroo-cyan" : selMeta.status === "preview" ? "bg-amber-400/15 text-amber-300" : "bg-klyroo-red/15 text-klyroo-red"}`}>{selMeta.status}</span>
          {selMeta.recommended && <span className="font-mono px-1.5 py-0.5 rounded bg-white/10 text-white">recommended</span>}
        </div>
      )}
      <Field label={`API Key${p.configured ? " (leave blank to keep current)" : ""}`}>
        <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off"
          placeholder={p.configured ? "•••••••••••• (stored)" : "Paste API key"} className={inputCls} data-testid={`llm-key-${p.id}`} />
      </Field>
      {result && (
        <p className={`mt-3 text-sm ${result.ok ? "text-klyroo-cyan" : "text-klyroo-red"}`} data-testid={`llm-result-${p.id}`}>
          {result.ok ? "✓ " : "✕ "}{result.message}
        </p>
      )}
      <div className="flex gap-3 mt-4">
        <button onClick={save} disabled={saving} data-testid={`llm-save-${p.id}`}
          className="px-4 py-2 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm hover:bg-white disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        <button onClick={test} disabled={testing || !p.configured} data-testid={`llm-test-${p.id}`}
          className="px-4 py-2 rounded-lg bg-white/5 text-slate-200 text-sm hover:bg-white/10 disabled:opacity-50">{testing ? "Testing…" : "Test Connection"}</button>
      </div>
    </div>
  );
}

function HomepageCuration() {
  const [articles, setArticles] = useState(null);
  const [featured, setFeatured] = useState("");
  const [trending, setTrending] = useState(Array(7).fill(""));
  const [latest, setLatest] = useState(Array(6).fill(""));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.adminList("articles"), api.adminGetHomepage()]).then(([a, cur]) => {
      setArticles(a.items || []);
      setFeatured(cur.featured || "");
      setTrending([...(cur.trending || []), ...Array(7).fill("")].slice(0, 7));
      setLatest([...(cur.latest || []), ...Array(6).fill("")].slice(0, 6));
    }).catch(() => setArticles([]));
  }, []);

  const setSlot = (arr, setArr, i, v) => { const c = [...arr]; c[i] = v; setArr(c); };

  const save = async () => {
    setSaving(true);
    try {
      await api.adminSaveHomepage({
        featured,
        trending: trending.filter(Boolean),
        latest: latest.filter(Boolean),
      });
      toast.success("Homepage curation saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!articles) return <Loader />;

  const opts = (
    <>
      <option value="">— Automatic —</option>
      {articles.map((a) => <option key={a.slug} value={a.slug}>{a.title}{a.status && a.status !== "published" ? ` (${a.status})` : ""}</option>)}
    </>
  );
  const Row = ({ n, value, onChange, testid }) => (
    <div className="flex items-center gap-3">
      <span className="w-6 shrink-0 text-right font-mono text-sm text-klyroo-cyan/60">{n}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls}>{opts}</select>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="homepage-curation">
      <p className="text-sm text-slate-400">Manually pick which existing articles appear in the hero, Trending, and Latest sections. Leave a slot on <span className="text-klyroo-cyan">Automatic</span> to keep the current auto behaviour for that section.</p>

      <div className="card-surface rounded-2xl p-5">
        <Overline className="mb-3">Featured Story</Overline>
        <select value={featured} onChange={(e) => setFeatured(e.target.value)} data-testid="curation-featured" className={inputCls}>{opts}</select>
        <p className="mt-2 text-xs text-slate-500">Shown as the large hero card (rotates with Trending). Automatic = newest AI article.</p>
      </div>

      <div className="card-surface rounded-2xl p-5">
        <Overline className="mb-3">Trending Now (up to 7)</Overline>
        <div className="space-y-2">
          {trending.map((v, i) => <Row key={i} n={i + 1} value={v} onChange={(val) => setSlot(trending, setTrending, i, val)} testid={`curation-trending-${i}`} />)}
        </div>
        <p className="mt-2 text-xs text-slate-500">Displayed in this order. Empty slots are skipped. All empty = automatic (most-viewed).</p>
      </div>

      <div className="card-surface rounded-2xl p-5">
        <Overline className="mb-3">Latest on KLYROO (up to 6)</Overline>
        <div className="space-y-2">
          {latest.map((v, i) => <Row key={i} n={i + 1} value={v} onChange={(val) => setSlot(latest, setLatest, i, val)} testid={`curation-latest-${i}`} />)}
        </div>
        <p className="mt-2 text-xs text-slate-500">Displayed in this order. All empty = automatic mix of newest content.</p>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="curation-save"
          className="px-6 py-2.5 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm hover:bg-white disabled:opacity-60">{saving ? "Saving…" : "Save Changes"}</button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="text-right">
      <div className={`font-display font-bold text-sm ${accent ? "text-klyroo-cyan" : "text-white"}`}>{value}</div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}

function JsonEditor({ coll, data, onClose, onSaved }) {
  const [json, setJson] = useState(JSON.stringify(data, null, 2));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    let parsed;
    try { parsed = JSON.parse(json); } catch { return toast.error("Invalid JSON"); }
    if (!parsed.slug) return toast.error("A 'slug' field is required");
    delete parsed.__new;
    setSaving(true);
    try { await api.adminUpsert(coll, parsed); toast.success("Saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-3xl h-[85vh] card-surface rounded-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <span className="font-display font-bold text-white capitalize">{data.__new ? `New ${coll.slice(0, -1)}` : `Edit ${data.slug}`}</span>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <p className="px-4 pt-3 text-xs font-mono text-slate-500">Visual editor for {coll} is coming in the next pass. For now, edit the document fields below. A unique <span className="text-klyroo-cyan">slug</span> is required.</p>
        <textarea value={json} onChange={(e) => setJson(e.target.value)} data-testid="admin-editor-json" spellCheck={false}
          className="flex-1 m-4 bg-klyroo-bg border border-white/10 rounded-xl p-4 font-mono text-xs text-slate-200 outline-none focus:border-klyroo-cyan/50 resize-none" />
        <div className="flex justify-end gap-3 p-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-slate-300 hover:bg-white/5 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="admin-save" className="px-5 py-2 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm hover:bg-white disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Ads form (kept from previous version) ---------------- */
function AdForm({ data, onClose, onSaved }) {
  const isNew = !!data.__new;
  const [f, setF] = useState(() => ({
    slug: data.slug || "", name: data.name || "", placement: data.placement || "home-top",
    sponsor: data.sponsor || "", headline: data.headline || "", subtext: data.subtext || "",
    cta: data.cta || "Learn more", link: data.link || "", image: data.image || "",
    active: data.active !== false,
  }));
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const onName = (v) => { set("name", v); if (isNew && !slugTouched) set("slug", slugify(v)); };
  const save = async () => {
    if (!f.slug) return toast.error("A slug is required (auto-fills from the ad name)");
    if (!f.image) return toast.error("Upload a banner image first");
    if (!f.link) return toast.error("A destination link is required");
    const payload = { ...data, ...f };
    delete payload.__new;
    setSaving(true);
    try { await api.adminUpsert("ads", payload); toast.success("Ad saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const placement = PLACEMENT_MAP[f.placement];
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-3xl h-[88vh] card-surface rounded-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <span className="font-display font-bold text-white">{isNew ? "New Ad" : `Edit "${data.name || data.slug}"`}</span>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Ad name (internal)"><input value={f.name} onChange={(e) => onName(e.target.value)} placeholder="e.g. NimbusCloud — Homepage Hero" data-testid="ad-name" className={inputCls} /></Field>
            <Field label="Slug (unique)"><input value={f.slug} onChange={(e) => { set("slug", slugify(e.target.value)); setSlugTouched(true); }} disabled={!isNew} data-testid="ad-slug" className={`${inputCls} ${!isNew ? "opacity-60 cursor-not-allowed" : ""}`} /></Field>
          </div>
          <Field label="Placement">
            <select value={f.placement} onChange={(e) => set("placement", e.target.value)} data-testid="ad-placement" className={inputCls}>
              {AD_PLACEMENTS.map((p) => <option key={p.id} value={p.id}>{`${p.label} — ${p.ratioLabel} · ${p.size}`}</option>)}
            </select>
            {placement && (
              <p className="mt-1.5 text-xs text-slate-500" data-testid="ad-placement-hint">
                Recommended image: <span className="text-klyroo-cyan font-mono">{placement.size}</span> ({placement.ratioLabel} ratio · {placement.fit === "contain" ? "shown uncropped" : "fills the slot"})
              </p>
            )}
          </Field>
          <BannerUpload image={f.image} placement={f.placement} onChange={(url) => set("image", url)} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Sponsor"><input value={f.sponsor} onChange={(e) => set("sponsor", e.target.value)} data-testid="ad-sponsor" className={inputCls} /></Field>
            <Field label="CTA label"><input value={f.cta} onChange={(e) => set("cta", e.target.value)} data-testid="ad-cta" className={inputCls} /></Field>
          </div>
          <Field label="Headline"><input value={f.headline} onChange={(e) => set("headline", e.target.value)} data-testid="ad-headline" className={inputCls} /></Field>
          <Field label="Subtext"><input value={f.subtext} onChange={(e) => set("subtext", e.target.value)} data-testid="ad-subtext" className={inputCls} /></Field>
          <Field label="Destination / Affiliate Link" hint="Full URL preserved exactly — UTM, tracking & affiliate params are never stripped.">
            <input value={f.link} onChange={(e) => set("link", e.target.value)} placeholder="https://…?ref=klyroo" data-testid="ad-link" className={inputCls} />
          </Field>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="ad-active" className="w-4 h-4 accent-klyroo-cyan" />
            <span className="text-sm text-slate-200">Active (visible on the site)</span>
          </label>
          {!isNew && (
            <div className="flex items-center gap-6 pt-2 border-t border-white/5">
              <Stat label="Impressions" value={(data.impressions || 0).toLocaleString()} />
              <Stat label="Clicks" value={(data.clicks || 0).toLocaleString()} />
              <Stat label="CTR" value={`${ctr(data.impressions, data.clicks)}%`} accent />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-white/10">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-slate-300 hover:bg-white/5 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="admin-save" className="px-5 py-2 rounded-lg bg-klyroo-cyan text-black font-semibold text-sm hover:bg-white disabled:opacity-60">{saving ? "Saving…" : "Save Ad"}</button>
        </div>
      </div>
    </div>
  );
}

const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;
function readRatio(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img.naturalWidth / img.naturalHeight); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

function BannerUpload({ image, placement, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [warn, setWarn] = useState("");
  const cfg = PLACEMENT_MAP[placement];
  const pick = () => inputRef.current && inputRef.current.click();
  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!ACCEPTED.includes(file.type)) return toast.error("Unsupported type. Use JPG, PNG or WebP.");
    if (file.size > MAX_BYTES) return toast.error("File too large. Max 5 MB.");
    setWarn("");
    if (cfg) {
      const r = await readRatio(file);
      if (r && Math.abs(r - cfg.ratio) / cfg.ratio > RATIO_TOLERANCE) {
        setWarn(`This image is ${r.toFixed(2)}:1 but ${cfg.label} expects ${cfg.ratioLabel} (${cfg.size}). It may look stretched or letterboxed.`);
        toast.warning("Aspect ratio doesn't match this placement");
      }
    }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try { const res = await api.adminUpload(form, "ads"); onChange(res.url); toast.success("Banner uploaded"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed. Please try again."); }
    finally { setUploading(false); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-klyroo-bg/60 p-4" data-testid="ad-banner-upload">
      <Overline className="mb-3">Banner Image</Overline>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" data-testid="ad-banner-file-input" />
      <div className="flex items-start gap-4">
        <div className="shrink-0 overflow-hidden rounded-lg border border-white/10 bg-klyroo-surface flex items-center justify-center" style={{ width: 160, aspectRatio: cfg?.aspect || "16 / 9" }}>
          {image ? <img src={proxyImg(image)} alt="Banner preview" data-testid="ad-banner-preview" className={`h-full w-full ${cfg?.fit === "contain" ? "object-contain bg-black" : "object-cover"}`} /> : <ImageIcon size={22} className="text-slate-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={pick} disabled={uploading} data-testid="ad-banner-upload-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-black text-sm font-semibold hover:bg-klyroo-cyan disabled:opacity-60"><Upload size={14} /> {uploading ? "Uploading…" : image ? "Replace Image" : "Upload Banner Image"}</button>
            {image && <button type="button" onClick={() => { onChange(""); setWarn(""); }} disabled={uploading} data-testid="ad-banner-remove-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 text-slate-300 text-sm hover:bg-white/10"><Trash2 size={14} /> Remove</button>}
          </div>
          <p className="mt-2 text-xs text-slate-500">JPG, PNG or WebP · up to 5 MB{cfg ? ` · recommended ${cfg.size}` : ""}.</p>
          {warn && <p className="mt-1.5 text-xs text-klyroo-amber" data-testid="ad-banner-warn">{warn}</p>}
          {image && <p className="mt-1 text-[11px] font-mono text-slate-600 truncate" data-testid="ad-banner-url">{image}</p>}
        </div>
      </div>
    </div>
  );
}
